import { useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import type {
  CreateEstimateDto,
  CustomerApprovalMethod,
  EstimateBookingType,
} from '@/types/estimate';
import {
  buildEstimateCustomFieldValues,
  buildEstimatePayload,
  formatEstimateAddress,
  type EditableLineItem,
  type EstimateContactAddress,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import type { CustomFieldSchema } from '@/types/custom-fields';
import { addressesMatch } from '@/lib/address-match';
import { sanitizePostalCodeInput } from '@/lib/postal-code';
import type { TermsSelection } from '@/lib/terms';

interface UseEstimateFormViewModelArgs {
  contactId: string | null;
  billToId?: string | null;
  estimateNumber: string;
  title: string;
  jobTypeId: string;
  jobTypeName: string;
  poNumber: string;
  includeTitle: boolean;
  baseMetadata?: Record<string, unknown>;
  linkedLeadId: string;
  source: string;
  leadSource: string;
  leadSourceId: string;
  salespersonId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  primaryContactName?: string;
  primaryContactTitle?: string;
  customerAddress: string;
  saStreet: string;
  saStreet2: string;
  saCity: string;
  saState: string;
  saZip: string;
  billingMode: 'select' | 'manual';
  selectedBillingIndex: number | null;
  baStreet: string;
  baStreet2: string;
  baCity: string;
  baState: string;
  baZip: string;
  storedBillingAddress?: Parameters<typeof buildEstimatePayload>[0]['storedBillingAddress'];
  contactAddresses: EstimateContactAddress[];
  lineItems: EditableLineItem[];
  lineItemPreviews: Array<{
    lineSubtotal: number;
    lineTax: number;
    lineTotal: number;
    snapshot?: unknown;
  }>;
  subtotal: number;
  taxTotal: number;
  discountAmount: number;
  discountValue: number;
  discountType: '$' | '%';
  discountReason: string;
  promotionId: string | null;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  customerApprovalMethod: CustomerApprovalMethod | '';
  requireSignature: boolean;
  grandTotal: number;
  depositAmount: number;
  depositRequired: boolean;
  depositType: '$' | '%';
  depositValue: number;
  bookingType: EstimateBookingType;
  recurringConfigPayload: CreateEstimateDto['recurringConfig'] | undefined;
  validUntil: string;
  scopeOfWork: string;
  notes: string;
  terms: TermsSelection;
  /** Document template to render through; null/undefined uses the tenant default. */
  docTemplateId?: string | null;
  internalNotes: string;
  tagIds: string[];
  pendingAttachmentId?: string;
  isEditMode: boolean;
  // NOT-542 — Business Unit. Sent only while the estimate may still choose one.
  includeBusinessUnit: boolean;
  businessUnitId: string | null;
  derivedBusinessUnitId: string | null;
  /** The unit picked in the create flow; stamped as SELECTED on create only. */
  selectedBusinessUnitId?: string;
  customFields: Record<string, string>;
  activeFieldKeys: string[];
  estimateCustomFields: CustomFieldSchema[];
  addressMode: 'select' | 'manual';
  selectedAddressIndex: number | null;
  applyAddress: (address: EstimateContactAddress) => void;
  handleZipChange: (zip: string, setZip: Dispatch<SetStateAction<string>>) => void;
  setAddressMode: Dispatch<SetStateAction<'select' | 'manual'>>;
  setSelectedAddressIndex: Dispatch<SetStateAction<number | null>>;
  setSaStreet: Dispatch<SetStateAction<string>>;
  setSaStreet2: Dispatch<SetStateAction<string>>;
  setSaCity: Dispatch<SetStateAction<string>>;
  setSaState: Dispatch<SetStateAction<string>>;
  setSaZip: Dispatch<SetStateAction<string>>;
  setBillingMode: Dispatch<SetStateAction<'select' | 'manual'>>;
  setSelectedBillingIndex: Dispatch<SetStateAction<number | null>>;
  setBaStreet: Dispatch<SetStateAction<string>>;
  setBaStreet2: Dispatch<SetStateAction<string>>;
  setBaCity: Dispatch<SetStateAction<string>>;
  setBaState: Dispatch<SetStateAction<string>>;
  setBaZip: Dispatch<SetStateAction<string>>;
  setCustomerAddress: Dispatch<SetStateAction<string>>;
}

function findPreferredAddressIndex(
  addresses: EstimateContactAddress[],
  predicate: (address: EstimateContactAddress) => boolean,
): number | null {
  const candidates = addresses.filter(predicate);
  const preferred = candidates.find((address) => address.isPrimary) ?? candidates[0];
  if (!preferred) return null;
  const index = addresses.indexOf(preferred);
  return index >= 0 ? index : null;
}

/** The row a switch into manual entry left behind, so the switch back can restore it. */
interface RememberedSelection {
  id?: string;
  address: EstimateContactAddress;
  contactId: string | null;
}

function rememberSelection(
  addresses: EstimateContactAddress[],
  index: number | null,
  contactId: string | null,
): RememberedSelection | null {
  if (index === null) return null;
  const address = addresses[index];
  if (!address) return null;
  return { id: address.id, address, contactId };
}

/**
 * Resolve a remembered row against the CURRENT list — never by the position it
 * held. An index would resolve to a different address as soon as the list
 * reordered or gained a row above the remembered one, which is the same silent
 * repointing this whole change exists to remove, just sourced from the fix.
 *
 * Rows carry an id, so that is the handle. Legacy rows saved before ids were
 * assigned fall back to matching the address itself with the normalized
 * comparison this PR introduces — position is never consulted. Either way an
 * address that is no longer in the list resolves to null and the caller takes
 * its flagged default, which is the right guess once the row is genuinely gone.
 *
 * The `contactId` gate is what stops a remembered row from resolving against a
 * DIFFERENT customer's addresses after the user switches contacts — two
 * contacts can easily share a service address, so content matching alone would
 * not catch it.
 */
function resolveRememberedIndex(
  addresses: EstimateContactAddress[],
  remembered: RememberedSelection | null,
  contactId: string | null,
): number | null {
  if (!remembered || remembered.contactId !== contactId) return null;
  const byId = remembered.id
    ? addresses.findIndex((address) => address.id === remembered.id)
    : addresses.findIndex((address) => addressesMatch(address, remembered.address));
  return byId >= 0 ? byId : null;
}

export function useEstimateFormViewModel({
  contactId,
  billToId,
  estimateNumber,
  title,
  jobTypeId,
  jobTypeName,
  poNumber,
  includeTitle,
  baseMetadata,
  linkedLeadId,
  source,
  leadSource,
  leadSourceId,
  salespersonId,
  customerName,
  customerEmail,
  customerPhone,
  primaryContactName,
  primaryContactTitle,
  customerAddress,
  saStreet,
  saStreet2,
  saCity,
  saState,
  saZip,
  billingMode,
  selectedBillingIndex,
  baStreet,
  baStreet2,
  baCity,
  baState,
  baZip,
  storedBillingAddress,
  contactAddresses,
  lineItems,
  lineItemPreviews,
  subtotal,
  taxTotal,
  discountAmount,
  discountValue,
  discountType,
  discountReason,
  promotionId,
  pricingMode,
  customerApprovalMethod,
  requireSignature,
  grandTotal,
  depositAmount,
  depositRequired,
  depositType,
  depositValue,
  bookingType,
  recurringConfigPayload,
  validUntil,
  scopeOfWork,
  notes,
  terms,
  docTemplateId,
  internalNotes,
  tagIds,
  pendingAttachmentId,
  isEditMode,
  includeBusinessUnit,
  businessUnitId,
  derivedBusinessUnitId,
  selectedBusinessUnitId,
  customFields,
  activeFieldKeys,
  estimateCustomFields,
  addressMode,
  selectedAddressIndex,
  applyAddress,
  handleZipChange,
  setAddressMode,
  setSelectedAddressIndex,
  setSaStreet,
  setSaStreet2,
  setSaCity,
  setSaState,
  setSaZip,
  setBillingMode,
  setSelectedBillingIndex,
  setBaStreet,
  setBaStreet2,
  setBaCity,
  setBaState,
  setBaZip,
  setCustomerAddress,
}: UseEstimateFormViewModelArgs) {
  const payload = useMemo(
    () =>
      buildEstimatePayload({
        contactId,
        billToId,
        estimateNumber,
        title,
        jobTypeId,
        jobTypeName,
        poNumber,
        includeTitle,
        baseMetadata,
        linkedLeadId,
        source,
        leadSource,
        leadSourceId,
        salespersonId,
        customerName,
        customerEmail,
        customerPhone,
        primaryContactName,
        primaryContactTitle,
        customerAddress,
        addressMode,
        selectedAddressIndex,
        saStreet,
        saStreet2,
        saCity,
        saState,
        saZip,
        billingMode,
        selectedBillingIndex,
        baStreet,
        baStreet2,
        baCity,
        baState,
        baZip,
        storedBillingAddress,
        contactAddresses,
        lineItems,
        lineItemPreviews,
        subtotal,
        taxTotal,
        discountAmount,
        discountValue,
        discountType,
        discountReason,
        promotionId,
        pricingMode,
        customerApprovalMethod,
        signatureRequired: customerApprovalMethod
          ? customerApprovalMethod === 'SIGNATURE'
          : requireSignature,
        grandTotal,
        depositAmount,
        depositRequired,
        depositType,
        depositValue,
        bookingType,
        recurringConfigPayload: recurringConfigPayload ?? null,
        validUntil,
        scopeOfWork,
        notes,
        terms,
        docTemplateId,
        internalNotes,
        tagIds,
        pendingAttachmentId,
        includePendingAttachmentId: !isEditMode,
        isEditMode,
        includeBusinessUnit,
        businessUnitId,
        derivedBusinessUnitId,
        selectedBusinessUnitId,
      }),
    [
      contactId,
      billToId,
      estimateNumber,
      title,
      jobTypeId,
      jobTypeName,
      poNumber,
      includeTitle,
      baseMetadata,
      linkedLeadId,
      source,
      leadSource,
      leadSourceId,
      salespersonId,
      customerName,
      customerEmail,
      customerPhone,
      primaryContactName,
      primaryContactTitle,
      customerAddress,
      addressMode,
      selectedAddressIndex,
      saStreet,
      saStreet2,
      saCity,
      saState,
      saZip,
      billingMode,
      selectedBillingIndex,
      baStreet,
      baStreet2,
      baCity,
      baState,
      baZip,
      storedBillingAddress,
      contactAddresses,
      lineItems,
      lineItemPreviews,
      subtotal,
      taxTotal,
      discountAmount,
      discountValue,
      discountType,
      discountReason,
      promotionId,
      pricingMode,
      customerApprovalMethod,
      requireSignature,
      grandTotal,
      depositAmount,
      depositRequired,
      depositType,
      depositValue,
      bookingType,
      recurringConfigPayload,
      validUntil,
      scopeOfWork,
      notes,
      terms,
      docTemplateId,
      internalNotes,
      tagIds,
      pendingAttachmentId,
      isEditMode,
      includeBusinessUnit,
      businessUnitId,
      derivedBusinessUnitId,
      selectedBusinessUnitId,
    ],
  );

  const customFieldValues = useMemo(
    () => buildEstimateCustomFieldValues(customFields, estimateCustomFields, activeFieldKeys),
    [activeFieldKeys, customFields, estimateCustomFields],
  );

  /**
   * NOT-586 — the row each half was showing when the user switched to manual
   * entry.
   *
   * Switching to manual nulls the selected index (select mode resolves the
   * address FROM that index, so it cannot keep pointing at one row while the
   * user types another). That made `select -> manual -> select` lossy: the
   * switch back had nothing to restore and fell through to the
   * `isService`/`isBilling` default. Whenever the estimate had been reopened
   * onto a row that no longer carries the flag — which the normalized-match
   * restore does for any saved snapshot differing only by, say, Canadian
   * postal-code spacing — that default is a DIFFERENT address, and the toggle
   * writes it into the fields. The user changed nothing and the next save
   * repointed the estimate.
   *
   * The live-selection branches below still win when there is one; this only
   * covers the case where the toggle itself is what discarded it.
   */
  const lastServiceSelection = useRef<RememberedSelection | null>(null);
  const lastBillingSelection = useRef<RememberedSelection | null>(null);

  const handleToggleAddressMode = useCallback(() => {
    if (addressMode === 'manual') {
      const currentAddress =
        selectedAddressIndex !== null ? contactAddresses[selectedAddressIndex] : null;
      // NOT-586 — an existing selection wins whatever its flags. Re-deciding it
      // on `isService` discarded a row the form had correctly restored (one
      // since un-flagged as a service address, which the picker offers back via
      // `unofferedServiceOption`) and selected a DIFFERENT place instead —
      // `applyAddress` below then overwrote the sa* fields with that row's, so
      // leaving manual entry silently repointed the estimate at an address the
      // user never chose. The filter still decides the DEFAULT when nothing is
      // selected, which is the separate question of what to guess.
      const nextIndex = currentAddress
        ? selectedAddressIndex
        : (resolveRememberedIndex(contactAddresses, lastServiceSelection.current, contactId) ??
          findPreferredAddressIndex(contactAddresses, (address) => address.isService !== false));
      const nextAddress = nextIndex !== null ? contactAddresses[nextIndex] : null;

      setAddressMode('select');
      setSelectedAddressIndex(nextIndex);
      if (nextAddress) {
        applyAddress(nextAddress);
      } else {
        setSaStreet('');
        setSaStreet2('');
        setSaCity('');
        setSaState('');
        setSaZip('');
      }
      return;
    }

    // Keep the selected address in the manual fields instead of blanking them.
    // buildEstimatePayload reads a blank manual address on edit as "the user
    // cleared it" and sends serviceAddress: null, so blanking here would delete
    // a stored address — along with its addressId back-pointer, its exemption
    // fields and its tax basis — on a toggle the user meant as "let me edit
    // this". Carrying the values over also makes an all-blank manual address
    // mean what the payload builder assumes it means.
    lastServiceSelection.current = rememberSelection(
      contactAddresses,
      selectedAddressIndex,
      contactId,
    );
    setAddressMode('manual');
    setSelectedAddressIndex(null);
  }, [
    addressMode,
    setAddressMode,
    selectedAddressIndex,
    applyAddress,
    contactAddresses,
    contactId,
    setSaStreet,
    setSaStreet2,
    setSaCity,
    setSaState,
    setSaZip,
    setSelectedAddressIndex,
  ]);

  const handleSelectServiceAddress = useCallback(
    (index: number, address: EstimateContactAddress) => {
      setSelectedAddressIndex(index);
      applyAddress(address);
    },
    [setSelectedAddressIndex, applyAddress],
  );

  const handleSaStateChange = useCallback(
    (value: string) => {
      setSaState(value.toUpperCase().slice(0, 2));
    },
    [setSaState],
  );

  const handleSaZipChange = useCallback(
    (value: string) => {
      handleZipChange(sanitizePostalCodeInput(value), setSaZip);
    },
    [handleZipChange, setSaZip],
  );

  const handleToggleBillingMode = useCallback(() => {
    if (billingMode === 'manual') {
      const hasBillingAddresses = contactAddresses.some((address) => address.isBilling);
      // Same two questions as the service half above, and the same answer. A
      // live `selectedBillingIndex` wins whatever its flags; the remembered row
      // covers the selection this toggle itself discarded on the way into
      // manual entry; only with neither does the `isBilling` filter get to
      // guess. Re-deciding unconditionally moved the estimate's billing
      // address on a `select -> manual -> select` the user made no edit in.
      const currentBillingAddress =
        selectedBillingIndex !== null ? contactAddresses[selectedBillingIndex] : null;
      const billingIndex = currentBillingAddress
        ? selectedBillingIndex
        : (resolveRememberedIndex(contactAddresses, lastBillingSelection.current, contactId) ??
          findPreferredAddressIndex(contactAddresses, (address) =>
            hasBillingAddresses ? Boolean(address.isBilling) : address.isService !== false,
          ));
      const billingAddress = billingIndex !== null ? contactAddresses[billingIndex] : null;

      setBillingMode('select');
      setSelectedBillingIndex(billingIndex);
      setCustomerAddress(billingAddress ? formatEstimateAddress(billingAddress) : '');
      // ba* deliberately left intact. buildEstimatePayload reads them only in
      // manual mode — select mode resolves the address from the index — so
      // they are inert here, and keeping them is what lets a user toggle back
      // without losing what they had. Blanking them made the round trip
      // destructive for the six derivation branches that select a null index.
      return;
    }

    // Same reasoning as handleToggleAddressMode. Hydration now seeds ba* even
    // in select mode, so the usual case is that they already hold the billing
    // address and simply survive this toggle. Re-seeding from the selection
    // when there is one keeps the most recent explicit choice winning; when
    // there is no selection, whatever hydration left is what carries over.
    //
    // This matters because `customerAddress` is a formatted display string
    // that cannot be turned back into fields — six of
    // findPreferredBillingSelection's branches return a null index with one
    // populated, and blanking ba* here made every one of them a silent delete
    // of the stored billing address on the next save.
    const selectedBillingAddress =
      selectedBillingIndex !== null ? (contactAddresses[selectedBillingIndex] ?? null) : null;

    lastBillingSelection.current = rememberSelection(
      contactAddresses,
      selectedBillingIndex,
      contactId,
    );
    setBillingMode('manual');
    setSelectedBillingIndex(null);
    setCustomerAddress('');
    if (selectedBillingAddress) {
      setBaStreet(selectedBillingAddress.street ?? '');
      setBaStreet2(selectedBillingAddress.street2 ?? '');
      setBaCity(selectedBillingAddress.city ?? '');
      setBaState(selectedBillingAddress.state ?? '');
      setBaZip(selectedBillingAddress.zip ?? '');
    }
  }, [
    billingMode,
    contactAddresses,
    contactId,
    selectedBillingIndex,
    setBillingMode,
    setSelectedBillingIndex,
    setCustomerAddress,
    setBaStreet,
    setBaStreet2,
    setBaCity,
    setBaState,
    setBaZip,
  ]);

  const handleSelectBillingAddress = useCallback(
    (index: number, address: EstimateContactAddress) => {
      setSelectedBillingIndex(index);
      setCustomerAddress(formatEstimateAddress(address));
      setBaStreet('');
      setBaStreet2('');
      setBaCity('');
      setBaState('');
      setBaZip('');
    },
    [
      setSelectedBillingIndex,
      setCustomerAddress,
      setBaStreet,
      setBaStreet2,
      setBaCity,
      setBaState,
      setBaZip,
    ],
  );

  const handleBaStateChange = useCallback(
    (value: string) => {
      setBaState(value.toUpperCase().slice(0, 2));
    },
    [setBaState],
  );

  const handleBaZipChange = useCallback(
    (value: string) => {
      const zip = sanitizePostalCodeInput(value);
      handleZipChange(zip, setBaZip);
    },
    [handleZipChange, setBaZip],
  );

  return {
    payload,
    customFieldValues,
    handleToggleAddressMode,
    handleSelectServiceAddress,
    handleSaStateChange,
    handleSaZipChange,
    handleToggleBillingMode,
    handleSelectBillingAddress,
    handleBaStateChange,
    handleBaZipChange,
  };
}
